import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js'
import { Form, Spinner } from 'react-bootstrap'
import Button from '@components/common/Button/Button.component'
import { EmptyPlaceholder } from '@components/common'
import { useGlobalLoadingActions } from '@hooks/useGlobalLoading'
import {
  createSetupIntent,
  confirmSavedPaymentMethod,
  type CreateSetupIntentResponse,
} from '@services/api/billing/payment-method.api'
import { extractApiError } from '@utils/apiError.util'
import { getEnv } from '@utils/env'

const STRIPE_SETTINGS_PATH = '/settings/insurance-and-payments/accounting'

export type SetupFormInnerProps = {
  patientId: string
  clinicId: string
  isDefault: boolean
  onDefaultChange: (checked: boolean) => void
  onSaveSuccess: () => void
  onSaveError: (message: string) => void
}

function SetupFormInner({
  patientId,
  clinicId,
  isDefault,
  onDefaultChange,
  onSaveSuccess,
  onSaveError,
}: SetupFormInnerProps): React.JSX.Element {
  const stripe = useStripe()
  const elements = useElements()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const { showLoading, hideLoading } = useGlobalLoadingActions()

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault()
    if (!stripe || !elements) return
    setErrorMessage(null)
    setIsSubmitting(true)
    showLoading({ container: '.add-payment-modal' })
    try {
      const { error, setupIntent } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: { return_url: window.location.href },
      })
      if (error) {
        const msg = error.message ?? 'Setup failed.'
        setErrorMessage(msg)
        onSaveError(msg)
        return
      }
      if (setupIntent?.status === 'succeeded') {
        await confirmSavedPaymentMethod({
          setup_intent_id: setupIntent.id,
          patient_id: patientId,
          clinic_id: clinicId,
          is_default: isDefault,
        })
        onSaveSuccess()
        return
      }
      if (
        setupIntent?.status === 'requires_action' &&
        setupIntent.client_secret
      ) {
        const { error: nextError, setupIntent: nextSetupIntent } =
          await stripe.handleNextAction({
            clientSecret: setupIntent.client_secret,
          })
        if (nextError) {
          const msg = nextError.message ?? 'Authentication failed.'
          setErrorMessage(msg)
          onSaveError(msg)
          return
        }
        if (nextSetupIntent?.status === 'succeeded') {
          await confirmSavedPaymentMethod({
            setup_intent_id: nextSetupIntent.id,
            patient_id: patientId,
            clinic_id: clinicId,
            is_default: isDefault,
          })
          onSaveSuccess()
          return
        }
        onSaveError('Setup could not be completed.')
        return
      }
      onSaveError('Setup could not be completed.')
    } catch (err: unknown) {
      const msg = extractApiError(err, 'Could not save card.')
      onSaveError(msg)
      setErrorMessage(msg)
    } finally {
      setIsSubmitting(false)
      hideLoading('.add-payment-modal')
    }
  }

  return (
    <Form onSubmit={handleSubmit}>
      <PaymentElement />
      {errorMessage && (
        <div className="text-danger small mt-2" role="alert">
          {errorMessage}
        </div>
      )}
      <Form.Check
        className="mt-3"
        type="checkbox"
        id="set-default-card"
        label="Make this my default card"
        checked={isDefault}
        onChange={(e) => onDefaultChange(e.target.checked)}
      />
      <Button
        type="submit"
        size="lg"
        className="button-primary button-apm-modal w-100 mt-3"
        disabled={!stripe || isSubmitting}
      >
        Add payment method
      </Button>
    </Form>
  )
}

export type CardFormProps = {
  patientId: string
  clinicId: string
  onSaveSuccess: () => void
  onSaveError?: (message: string) => void
  onClose: () => void
}

const CardForm: React.FC<CardFormProps> = ({
  patientId,
  clinicId,
  onSaveSuccess,
  onSaveError,
  onClose,
}) => {
  const navigate = useNavigate()
  const [isDefault, setIsDefault] = useState(false)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [stripeConnectedAccountId, setStripeConnectedAccountId] = useState<
    string | null
  >(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [needsStripeSetup, setNeedsStripeSetup] = useState(false)

  const stripePromise = useMemo(() => {
    const publishableKey = getEnv('REACT_APP_STRIPE_PUBLISHABLE_KEY', '')
    if (!publishableKey || !stripeConnectedAccountId) {
      return null
    }
    return loadStripe(publishableKey, {
      stripeAccount: stripeConnectedAccountId,
    })
  }, [stripeConnectedAccountId])

  useEffect(() => {
    let cancelled = false
    const run = async (): Promise<void> => {
      if (!patientId || !clinicId) {
        setLoading(false)
        return
      }
      const publishableKey = getEnv('REACT_APP_STRIPE_PUBLISHABLE_KEY', '')
      if (!publishableKey) {
        if (!cancelled) {
          setNeedsStripeSetup(true)
          setLoading(false)
        }
        return
      }
      setLoading(true)
      setError(null)
      setNeedsStripeSetup(false)
      try {
        const res: CreateSetupIntentResponse = await createSetupIntent(
          patientId,
          clinicId,
          ['card'],
        )
        const data = res.data
        if (
          !data?.client_secret ||
          !data?.stripe_connected_account_id?.startsWith('acct_')
        ) {
          throw new Error('Invalid setup intent response')
        }
        if (!cancelled) {
          setClientSecret(data.client_secret)
          setStripeConnectedAccountId(data.stripe_connected_account_id)
        }
      } catch {
        if (cancelled) return
        setNeedsStripeSetup(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [patientId, clinicId])

  const handleError = useCallback(
    (message: string): void => {
      setError(message)
      onSaveError?.(message)
    },
    [onSaveError],
  )

  const handleGoToStripeSettings = useCallback((): void => {
    onClose()
    void navigate(STRIPE_SETTINGS_PATH)
  }, [navigate, onClose])

  if (loading) {
    return (
      <div className="d-flex align-items-center gap-2">
        <Spinner animation="border" size="sm" />
        <span>Loading payment form...</span>
      </div>
    )
  }

  if (needsStripeSetup) {
    return (
      <EmptyPlaceholder
        title="Stripe is not set up"
        content="Go to accounting settings to setup Stripe."
        icon="mgc_currency_dollar_fill"
        buttonText="Go to accounting settings"
        onAdd={handleGoToStripeSettings}
      />
    )
  }

  if (error && !clientSecret) {
    return (
      <div className="text-danger small" role="alert">
        {error}
      </div>
    )
  }

  if (!clientSecret || !stripeConnectedAccountId) {
    return (
      <div className="text-muted small" role="status">
        Unable to load payment form. Please try again.
      </div>
    )
  }

  if (!stripePromise) {
    return (
      <div className="text-muted small" role="status">
        Unable to load payment form. Please try again.
      </div>
    )
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{ clientSecret, appearance: { theme: 'stripe' } }}
    >
      <SetupFormInner
        patientId={patientId}
        clinicId={clinicId}
        isDefault={isDefault}
        onDefaultChange={setIsDefault}
        onSaveSuccess={onSaveSuccess}
        onSaveError={handleError}
      />
    </Elements>
  )
}

export default CardForm
